import { useState, useEffect } from 'react';
import { getStudentData } from '../assets/data';
import { getLessonListForStudent } from '../utils/lessonsService';
import { getStudentByInviteSlug } from '../utils/studentsService';
import { getHomework, getHomeworkById, deleteHomework } from '../utils/homeworkService';

// Extracted from StudentProfile.js (Phase 3 of the platform-health plan:
// getting the file under ~600 lines needs pulling logic into hooks, not
// just JSX into components). Owns loading, filtering, opening, and
// deleting a student's homework list.
export function useHomeworkManager({ studentId, isNew, navigate }) {
  const [homeworkFilter, setHomeworkFilter] = useState('all'); // 'all', 'assigned', 'submitted', 'reviewed'
  const [studentHomework, setStudentHomework] = useState([]);
  const [homeworkLoading, setHomeworkLoading] = useState(true);

  // Загружаем homework студента
  useEffect(() => {
    const loadStudentHomework = () => {
      if (studentId === "new" || isNew || !studentId) {
        setStudentHomework([]);
        setHomeworkLoading(false);
        return;
      }

      const homeworkData = [];
      const currentStudent = getStudentData()[studentId];
      const studentIdForLessons = currentStudent?.studentId || currentStudent?.id || studentId;
      if (!studentIdForLessons) {
        setStudentHomework([]);
        setHomeworkLoading(false);
        return;
      }

      setHomeworkLoading(true);
      getLessonListForStudent(studentIdForLessons).then(async (studentLessons) => {
      const studentLessonIds = studentLessons.map(lesson => lesson.id);
      console.log('Student lessons:', studentLessons);
      console.log('Student lesson IDs:', studentLessonIds);

      // Определяем UUID студента для Supabase
      // studentIdForLessons может быть UUID или slug, нужно получить UUID
      let studentUuidForHomework = currentStudent?.studentId || currentStudent?.id;

      // Проверяем, является ли это UUID
      const isUUID = studentUuidForHomework && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(studentUuidForHomework));

      // Если UUID не найден или это не UUID, пробуем получить из Supabase по slug
      if (!isUUID && studentId) {
        try {
          const studentFromSupabase = await getStudentByInviteSlug(studentId);
          if (studentFromSupabase && studentFromSupabase.id) {
            studentUuidForHomework = studentFromSupabase.id;
          }
        } catch (e) {
          console.log('Could not get student UUID from Supabase:', e);
        }
      }

      // Если все еще нет UUID, используем studentIdForLessons (может быть UUID)
      if (!studentUuidForHomework) {
        studentUuidForHomework = studentIdForLessons;
      }

      // Загружаем домашние задания из Supabase
      try {
        const supabaseHomework = await getHomework(studentUuidForHomework);
        if (supabaseHomework && supabaseHomework.length > 0) {
          // Фильтруем домашние задания, которые назначены студенту или связаны с его уроками
          const filteredHomework = supabaseHomework.filter(homework => {
            const isAssignedToStudent = homework.studentId === studentId || homework.studentId === studentIdForLessons;
            const isDemoStudent = homework.studentId === 'demo-student-1';
            const isRelatedToStudentLesson = homework.lessonId && studentLessonIds.includes(homework.lessonId);
            return isAssignedToStudent || isDemoStudent || isRelatedToStudentLesson;
          });
          homeworkData.push(...filteredHomework);
          console.log('Loaded homework from Supabase:', filteredHomework);
        }
      } catch (error) {
        console.error('Error loading homework from Supabase:', error);
      }

      // Не загружаем из localStorage - только из Supabase

      // Дедупликация по ID (на случай, если все же есть дубликаты)
      const uniqueHomework = [];
      const seenIds = new Set();
      for (const hw of homeworkData) {
        const hwId = String(hw.id);
        if (!seenIds.has(hwId)) {
          seenIds.add(hwId);
          uniqueHomework.push(hw);
        }
      }

      // Сортируем по дате сдачи
      const sortedHomework = uniqueHomework.sort((a, b) => {
        const dateA = new Date(a.dueDate || a.created_at || 0);
        const dateB = new Date(b.dueDate || b.created_at || 0);
        return dateA - dateB;
      });
      console.log('Final homework for student (from Supabase only):', sortedHomework);
      setStudentHomework(sortedHomework);
      setHomeworkLoading(false);
      }).catch(error => {
        console.error('Error loading student lessons for homework:', error);
        setStudentHomework([]);
        setHomeworkLoading(false);
      });
    };

    loadStudentHomework();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const handleCheckHomework = async (homeworkId) => {
    let lessonId = null;
    try {
      const fromSupabase = await getHomeworkById(homeworkId);
      if (fromSupabase?.lessonId) lessonId = fromSupabase.lessonId;
    } catch (_) {}
    if (!lessonId) {
      const homeworkData = JSON.parse(localStorage.getItem(`homework_${homeworkId}`) || '{}');
      lessonId = homeworkData.lessonId || 'new';
    }
    navigate(`/lessons/${lessonId}?homeworkId=${homeworkId}&studentId=${studentId}`);
  };

  const handleDeleteHomework = async (homeworkId) => {
    // Подтверждение удаления
    if (!window.confirm('Are you sure you want to delete this homework? This action cannot be undone.')) {
      return;
    }

    try {
      // Удаляем homework из Supabase
      await deleteHomework(homeworkId);
      console.log('Homework deleted from Supabase:', homeworkId);
    } catch (error) {
      console.error('Error deleting homework from Supabase:', error);
      // Продолжаем удаление из localStorage даже если Supabase удаление не удалось
    }

    // Удаляем homework из localStorage
    localStorage.removeItem(`homework_${homeworkId}`);

    // Обновляем список homework
    setStudentHomework(prev => prev.filter(h => h.id !== homeworkId));

    // Отправляем событие об удалении homework
    window.dispatchEvent(new CustomEvent('homeworkUpdated', {
      detail: { homeworkId, deleted: true }
    }));
  };

  return {
    homeworkFilter, setHomeworkFilter,
    studentHomework,
    homeworkLoading,
    handleCheckHomework,
    handleDeleteHomework,
  };
}
